import apiClient from "../apiClient";

import type { LoginCode, SSEData, UserInfo, UserToken } from "#/entity";

export interface SignInReq {
	username: string;
	password: string;
}

export interface PasswordLoginReq {
	phone: string;
	password: string;
}

export interface SignUpReq extends SignInReq {
	email: string;
}
export type SignInRes = UserToken & { user: UserInfo };
export interface PasswordLoginRes {
	token?: string | UserToken;
	phone?: string;
	user_id?: string | number;
	userId?: string | number;
	name?: string;
	sname?: string;
	avatar?: string;
	account?: string;
}

export enum UserApi {
	SignIn = "/staff/login/pwd",
	PasswordLogin = "/login/phone/pwd",
	SignUp = "/auth/signup",
	Logout = "/auth/logout",
	Refresh = "/auth/refresh",
	User = "/user",
	GetCode = "login/code",
	CodeEvent = "login/code/event",
	TestGetToken = "/test/GetToken", // 测试获取Token
	CheckLoginAndStore = "/test/CheckLoginAndStore", // 登录校验+权限+门店信息（三合一）
}

export enum StoreApi {
	CheckBrandUser = "store/sku/check",
	BillSummary = "store/bill/summary",
	PageProduct = "/store/product/page",
	DeleteProduct = "/store/product/delete",
	SaveProduct = "/store/product/save",
	PageSku = "/store/sku/page",
	FirstPush = "/store/sku/first_push",
	TreeChannel = "/mall/TreeChannel",
	Calculate = "/store/sku/calculate",
	EditPush = "/store/sku/edit_push",
	Unpublish = "/store/sku/unpublish",
	Publish = "/store/sku/publish",
	DeleteSku = "/store/sku/delete",
	SkuDetail = "store/sku/detail",
	GeoTree = "store/geo/tree",
	ListSession = "auction/ListSession",
	ListCategory = "/auction/ListCategory",
}

export enum MedalApi {
	PageAssetItems = "medal/PageAssetItems",
}

//订单
export enum OrderApi {
	ExpressComList = "/order/ExpressComList",
	DeliveryBatch = "/order/DeliveryBatch",
}

//订单
export enum StoreOrderApi {
	Page = "/store/order/page",
	Delivery = "/store/order/delivery",
	Count = "/store/order/count",
	DeliveryCompany = "/store/order/delivery/company",
	DeliveryUpdate = "/store/order/delivery/update",
	DeliveryBatch = "/store/order/deliveryBatch",
}

// 结算
export enum SettleApi {
	SearchBill = "/store/bill/page",
	BillSummary = "/store/bill/summary",
	WithdrawPrecheck = "/store/withdraw/precheck",
	WithdrawBatchBillList = "/store/withdraw/bill/list",
	UpdateWithdrawBatchBills = "/store/withdraw/bill/update",
	WithdrawSubmit = "/store/withdraw/submit",
	PageWithdrawRecord = "/store/withdraw/page",
}

const signin = (data: SignInReq) => apiClient.post<SignInRes>({ url: UserApi.SignIn, data });
const passwordLogin = (data: PasswordLoginReq) => apiClient.post<{ code: number; data: PasswordLoginRes; msg: string }>({ url: UserApi.PasswordLogin, data });
const signup = (data: SignUpReq) => apiClient.post<SignInRes>({ url: UserApi.SignUp, data });
const logout = () => apiClient.get({ url: UserApi.Logout });
const testLogin = (userId: string) => apiClient.get<any>({ url: UserApi.TestGetToken, params: { userId } }); // 测试登录
const checkLoginAndStore = (phone: string, skuType?: string) =>
	apiClient.get<any>({ url: UserApi.CheckLoginAndStore, params: { phone, ...(skuType ? { skuType } : {}) } });

const findById = (id: string) => apiClient.get<UserInfo[]>({ url: `${UserApi.User}/${id}` });
const getCode = () => apiClient.get<{ code: number; data: LoginCode; msg: string }>({ url: UserApi.GetCode });
const checkBrandUser = () => apiClient.get<{ code: number; data: { isBrandUser: boolean; tags?: string[] }; msg: string }>({ url: StoreApi.CheckBrandUser });
const getStoreBillSummary = () =>
	apiClient.get<{ code: number; data: { name?: string; avatar?: string; sname?: string }; msg: string }>({ url: StoreApi.BillSummary });
const expressComList = () => apiClient.get<any>({ url: OrderApi.ExpressComList });
const storeOrderPage = (data: any) => apiClient.get<any>({ url: StoreOrderApi.Page, params: data });
const storeOrderDelivery = (data: any) => apiClient.post<any>({ url: StoreOrderApi.Delivery, data });
const storeOrderCount = () => apiClient.get<any>({ url: StoreOrderApi.Count });
const storeOrderDeliveryCompany = () => apiClient.get<any>({ url: StoreOrderApi.DeliveryCompany });
const storeOrderDeliveryUpdate = (data: any) => apiClient.post<any>({ url: StoreOrderApi.DeliveryUpdate, data });
const storeOrderDeliveryBatch = (data: any) => apiClient.post<any>({ url: StoreOrderApi.DeliveryBatch, data });
const searchBill = (data: any) => apiClient.get<any>({ url: SettleApi.SearchBill, params: data });
const billSummary = () => apiClient.get<any>({ url: SettleApi.BillSummary });
const withdrawPrecheck = (data: any) => apiClient.post<any>({ url: SettleApi.WithdrawPrecheck, data });
const withdrawBatchBillList = (data: any) => apiClient.get<any>({ url: SettleApi.WithdrawBatchBillList, params: data });
const updateWithdrawBatchBills = (data: any) => apiClient.post<any>({ url: SettleApi.UpdateWithdrawBatchBills, data });
const withdrawSubmit = (data: any) => apiClient.post<any>({ url: SettleApi.WithdrawSubmit, data });
const withdrawApply = (data: any) => withdrawSubmit(data);
const pageWithdrawRecord = (data: any) => apiClient.get<any>({ url: SettleApi.PageWithdrawRecord, params: data });
const deliveryBatch = (data: any) => apiClient.post<any>({ url: OrderApi.DeliveryBatch, data });
const pageProduct = (data: any) => apiClient.get<any>({ url: StoreApi.PageProduct, params: data });
const deleteProduct = (data: any) => apiClient.post<any>({ url: StoreApi.DeleteProduct, data });
const saveProduct = (data: any) => apiClient.post<any>({ url: StoreApi.SaveProduct, data });
const pageSku = (data: any) => apiClient.get<any>({ url: StoreApi.PageSku, params: data });
const firstPush = (data: any) => apiClient.post<any>({ url: StoreApi.FirstPush, data });
const treeChannel = (data: any) => apiClient.get<any>({ url: StoreApi.TreeChannel, params: data });
const calculate = (data: any) => apiClient.post<any>({ url: StoreApi.Calculate, data });
const editPush = (data: any) => apiClient.post<any>({ url: StoreApi.EditPush, data });
const unpublish = (data: any) => apiClient.post<any>({ url: StoreApi.Unpublish, data });
const publish = (data: any) => apiClient.post<any>({ url: StoreApi.Publish, data });
const deleteSku = (data: any) => apiClient.post<any>({ url: StoreApi.DeleteSku, data });

const skuDetail = (data: any) => apiClient.get<any>({ url: StoreApi.SkuDetail, params: data });

const geoTree = (data: any) => apiClient.get<any>({ url: StoreApi.GeoTree, params: data });
const listSession = () => apiClient.get<any>({ url: StoreApi.ListSession });
const listCategory = () => apiClient.get<any>({ url: StoreApi.ListCategory });
const pageAssetItems = (data: any) => apiClient.get<any>({ url: MedalApi.PageAssetItems, params: data });

// SSE连接方法
const createSSEConnection = (code: string, onMessage: (message: SSEData) => void, onError?: (error: Event) => void) => {
	const eventSource = new EventSource(`${import.meta.env.VITE_APP_BASE_API}/${UserApi.CodeEvent}?code=${code}`);

	eventSource.addEventListener("code.login", (event) => {
		try {
			const message: SSEData = JSON.parse((event as MessageEvent).data);
			onMessage(message);
		} catch (error) {
			console.error("SSE消息解析失败:", error);
		}
	});

	eventSource.onerror = (error) => {
		console.error("SSE连接错误:", error);
		onError?.(error);
	};

	return eventSource;
};

export default {
	signin,
	passwordLogin,
	signup,
	findById,
	logout,
	getCode,
	testLogin,
	checkLoginAndStore,
	checkBrandUser,
	getStoreBillSummary,
	createSSEConnection,
	expressComList,
	storeOrderPage,
	storeOrderDelivery,
	storeOrderCount,
	storeOrderDeliveryCompany,
	storeOrderDeliveryUpdate,
	storeOrderDeliveryBatch,
	searchBill,
	billSummary,
	withdrawPrecheck,
	withdrawBatchBillList,
	updateWithdrawBatchBills,
	withdrawSubmit,
	withdrawApply,
	pageWithdrawRecord,
	deliveryBatch,
	pageProduct,
	deleteProduct,
	saveProduct,
	pageSku,
	firstPush,
	treeChannel,
	calculate,
	editPush,
	unpublish,
	publish,
	deleteSku,
	skuDetail,
	geoTree,
	listSession,
	listCategory,
	pageAssetItems,
};
